import { observer } from 'mobx-react';
import React, { useEffect, useState, useRef } from 'react';
import { ClaimEditorStore } from '../../stores/claim-editor';
import { sectionDomId } from '../../stores/claim-editor/claim-editor-sections';
import { BaseButton } from '../base/button';
import { Spinner } from '../base/spinner';
import { BaseDialog } from '../base/dialog/dialog';
import { AdditionalInformationForm } from './forms/additional-information-form';
import { AdditionalProvidersForm } from './forms/additional-providers-form';
import { BillingProviderForm } from './forms/additional-providers-form/billing-provider-form';
import { FacilityForm } from './forms/facility-form';
import { PatientForm } from './forms/patient-form';
import { SubscriberPayerList } from './forms/subscriber-payer-form';
import { ServicesList } from './forms/services-form/services-list';
import { GuarantorForm } from './forms/guarantor-form';
import { SettingsForm } from './forms/settings-form';
import { Latin1ValidationProvider, useLatin1ValidationContext } from '../contexts/latin1-validation-context';
import {
    ClaimEditorNav,
    ClaimEditorStickyFooter,
    ClaimErrorSummaryBanner,
    ClaimSuccessPanel,
    SectionStatusPill,
} from './claim-editor-chrome';
import * as S from './claim-editor.styles';
import * as ErrorStyles from '../../services/auth/error.styles';

interface IClaimEditorProps {
    claimEditorStore: ClaimEditorStore;
    claimId?: string;
    isTemplate?: boolean;
    onCancel?: () => void;
    onSuccess?: (claimId: string) => void;
    disabledSections?: string[];
    customSections?: React.ReactNode;
    headerActions?: React.ReactNode;
}

const ClaimEditorContent: React.FunctionComponent<IClaimEditorProps> = observer(props => {
    const {
        claimEditorStore,
        claimId,
        isTemplate = false,
        onCancel,
        onSuccess,
        disabledSections = [],
        customSections,
        headerActions,
    } = props;

    const hasClaimIdParam = Boolean(claimId);
    const isCreateMode = claimEditorStore?.isCreateClaim ?? !hasClaimIdParam;
    const isEditMode = !isCreateMode;
    // Subscribe so Formik children re-render when edit-load validation fires.
    void claimEditorStore?.validationSignal;
    void claimEditorStore?.validationEvaluated;
    const pageTitle = isCreateMode ? 'Create Claim' : 'Edit Claim';
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [createdClaimId, setCreatedClaimId] = useState<string | null>(null);
    const textRef = useRef<HTMLTextAreaElement>(null);
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const didRunEditValidationOnLoadRef = useRef(false);
    /**
     * "Create another" resets the store while the `claimId` prop is still present, which used to
     * satisfy the initial-loader condition below and strand the page on a spinner — the load
     * effect never re-ran because its dependencies had not changed. This suppresses the loader
     * for that flow until the editor is pointed at a different claim.
     */
    const [creatingAnother, setCreatingAnother] = useState(false);
    const { getErrors } = useLatin1ValidationContext();

    useEffect(() => {
        didRunEditValidationOnLoadRef.current = false;
        setCreatedClaimId(null);
        setBannerDismissed(false);
        setCreatingAnother(false);

        if (hasClaimIdParam && claimId && claimEditorStore) {
            void claimEditorStore.loadClaimData(claimId, isTemplate);
        }

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            setIsSubmitting(false);
            // Invalidate the in-flight load SYNCHRONOUSLY. The reset below is deferred, and a
            // response landing in that window still passed the token check and repopulated this
            // singleton store — showing the previous claim's data in the next editor.
            claimEditorStore?.cancelActiveLoad();
            // Defer PHI wipe so a remount of this effect (same claim) does not
            // clear the payload and put the full-page spinner back up. Timer lives on the
            // store: create vs edit are different routes, so a new instance cannot cancel
            // a ref on the unmounted one.
            claimEditorStore?.scheduleDeferredReset();
        };
    }, [claimId, hasClaimIdParam, claimEditorStore, isTemplate]);

    useEffect(() => {
        if (!claimEditorStore) return;
        if (!isEditMode) return;
        if (claimEditorStore.isLoading) return;
        if (didRunEditValidationOnLoadRef.current) return;

        didRunEditValidationOnLoadRef.current = true;
        // Wait until Formik trees have mounted after the loading spinner unmounts.
        const timer = window.setTimeout(() => {
            claimEditorStore.triggerValidation();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [claimEditorStore, isEditMode, claimEditorStore.isLoading]);

    const handleSubmit = async () => {
        if (!claimEditorStore) return;

        setIsSubmitting(true);
        setBannerDismissed(false);

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                await Promise.all([
                    claimEditorStore.preloadPayers(),
                    claimEditorStore.preloadProviders(),
                ]);
                claimEditorStore.syncInactiveCatalogIds();

                claimEditorStore.triggerValidation();

                const latin1Errors = getErrors();
                const blockers = claimEditorStore.getSubmitBlockers();

                if (!claimEditorStore.canSubmit || latin1Errors.length > 0) {
                    setIsSubmitting(false);
                    // Latin-1 charset issues are not in the field-error registry — surface via dialog.
                    if (latin1Errors.length > 0 && blockers.length === 0) {
                        setErrorMessage(`Cannot submit:\n- ${latin1Errors.join('\n- ')}`);
                    }
                    requestAnimationFrame(() => {
                        document.getElementById('claim-error-banner')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    });
                    return;
                }
                try {
                    const submittedAsCreate = claimEditorStore.isCreateClaim;
                    const newId = await claimEditorStore.submitClaim();
                    if (newId) {
                        if (submittedAsCreate) {
                            setCreatedClaimId(newId);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else if (onSuccess) {
                            onSuccess(newId);
                        }
                    } else {
                        setErrorMessage('An error occurred while submitting the claim.');
                    }
                } catch (error) {
                    const errorMsg =
                        error instanceof Error ? error.message : 'An error occurred while submitting the claim.';
                    setErrorMessage(errorMsg);
                }
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'An error occurred during validation.';
                setErrorMessage(errorMsg);
            } finally {
                setIsSubmitting(false);
            }
        }, 750);
    };

    const handleCloseError = () => {
        setErrorMessage(null);
    };

    const handleContinue = () => {
        handleCloseError();
    };

    const handleViewClaim = () => {
        if (createdClaimId && onSuccess) {
            onSuccess(createdClaimId);
        }
    };

    const handleCreateAnother = () => {
        setCreatedClaimId(null);
        setBannerDismissed(false);
        setCreatingAnother(true);
        claimEditorStore?.cancelActiveLoad();
        claimEditorStore?.resetStore();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderActions = () => {
        return (
            <ErrorStyles.DialogActions>
                <ErrorStyles.DialogButton variant="primary" onClick={handleContinue}>
                    Close
                </ErrorStyles.DialogButton>
            </ErrorStyles.DialogActions>
        );
    };

    const selectAll = () => {
        if (textRef && textRef.current) {
            textRef.current.select();
        }
    };

    const loadError = claimEditorStore?.loadError;
    const showInitialLoader =
        Boolean(hasClaimIdParam) &&
        !isSubmitting &&
        !creatingAnother &&
        !loadError &&
        claimEditorStore?.loadedClaimId !== claimId;

    if (showInitialLoader) {
        return (
            <S.LoadingContainer>
                <Spinner />
            </S.LoadingContainer>
        );
    }

    // A failed load leaves `loadedClaimId` null, so without this the spinner above never stops.
    // Say what happened instead of spinning forever.
    if (loadError && claimEditorStore?.loadedClaimId !== claimId) {
        return (
            <S.LoadingContainer>
                <ErrorStyles.DialogBody>This claim could not be loaded. {loadError.message}</ErrorStyles.DialogBody>
                <ErrorStyles.DialogActions>
                    <ErrorStyles.DialogButton
                        variant="primary"
                        onClick={() => claimId && claimEditorStore?.loadClaimData(claimId, isTemplate)}
                    >
                        Try again
                    </ErrorStyles.DialogButton>
                    {onCancel && (
                        <ErrorStyles.DialogButton variant="outlined" onClick={onCancel}>
                            Back
                        </ErrorStyles.DialogButton>
                    )}
                </ErrorStyles.DialogActions>
            </S.LoadingContainer>
        );
    }

    if (createdClaimId) {
        return (
            <S.Container>
                <ClaimSuccessPanel
                    claimId={createdClaimId}
                    onViewClaim={handleViewClaim}
                    onCreateAnother={handleCreateAnother}
                />
            </S.Container>
        );
    }

    const submitLabel = claimEditorStore?.isCreateClaim ? 'Create Claim' : 'Save & Send to Processing';

    return (
        <S.Container>
            <S.Layout>
                <ClaimEditorNav
                    claimEditorStore={claimEditorStore}
                    disabledSections={disabledSections}
                    claimId={isEditMode ? claimId : undefined}
                />

                <S.MainColumn>
                    <ClaimErrorSummaryBanner
                        claimEditorStore={claimEditorStore}
                        dismissed={bannerDismissed}
                        onDismiss={() => setBannerDismissed(true)}
                        claimValidationErrors={claimEditorStore?.claimDetails?.claim?.claimValidationErrors}
                    />

                    <S.Content>
                        {!disabledSections.includes('patient') && (
                            <S.Section id={sectionDomId('patient')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Patient Information</S.SectionTitle>
                                    <SectionStatusPill sectionId="patient" claimEditorStore={claimEditorStore} />
                                </S.SectionHeaderRow>
                                <PatientForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {!disabledSections.includes('guarantor') && (
                            <S.Section id={sectionDomId('guarantor')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Guarantor Information</S.SectionTitle>
                                    <SectionStatusPill sectionId="guarantor" claimEditorStore={claimEditorStore} />
                                    <div style={{ flex: 1 }} />
                                    <BaseButton
                                        type="button"
                                        variant="outlined"
                                        onClick={(): void => {
                                            claimEditorStore.copyGuarantorFromPatient();
                                        }}
                                        disabled={!claimEditorStore?.patient}>
                                        Copy From Patient
                                    </BaseButton>
                                </S.SectionHeaderRow>
                                <GuarantorForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {!disabledSections.includes('subscriber') && (
                            <S.Section id={sectionDomId('subscriber')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Subscriber Information</S.SectionTitle>
                                    <SectionStatusPill sectionId="subscriber" claimEditorStore={claimEditorStore} />
                                </S.SectionHeaderRow>
                                <SubscriberPayerList
                                    initialItems={(claimEditorStore.activePayload as any)?.subscribers}
                                    claimEditorStore={claimEditorStore}
                                />
                            </S.Section>
                        )}

                        <S.Section id={sectionDomId('services')}>
                            <S.SectionHeaderRow>
                                <S.SectionTitle>Services</S.SectionTitle>
                                <SectionStatusPill sectionId="services" claimEditorStore={claimEditorStore} />
                            </S.SectionHeaderRow>
                            <ServicesList claimEditorStore={claimEditorStore} />
                        </S.Section>

                        {!disabledSections.includes('provider') && (
                            <S.Section id={sectionDomId('billingProvider')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Billing Provider Information</S.SectionTitle>
                                    <SectionStatusPill
                                        sectionId="billingProvider"
                                        claimEditorStore={claimEditorStore}
                                    />
                                </S.SectionHeaderRow>
                                <BillingProviderForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {!disabledSections.includes('facility') && (
                            <S.Section id={sectionDomId('facility')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Facility Information</S.SectionTitle>
                                    <SectionStatusPill sectionId="facility" claimEditorStore={claimEditorStore} />
                                </S.SectionHeaderRow>
                                <FacilityForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {!disabledSections.includes('additionalProviders') && (
                            <S.Section id={sectionDomId('additionalProviders')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Additional Providers</S.SectionTitle>
                                    <SectionStatusPill
                                        sectionId="additionalProviders"
                                        claimEditorStore={claimEditorStore}
                                    />
                                </S.SectionHeaderRow>
                                <AdditionalProvidersForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {!disabledSections.includes('additionalInformation') && (
                            <S.Section id={sectionDomId('additionalInformation')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Additional Information</S.SectionTitle>
                                    <SectionStatusPill
                                        sectionId="additionalInformation"
                                        claimEditorStore={claimEditorStore}
                                    />
                                </S.SectionHeaderRow>
                                <AdditionalInformationForm
                                    claimEditorStore={claimEditorStore}
                                    isEditMode={isEditMode}
                                />
                            </S.Section>
                        )}

                        {!disabledSections.includes('settings') && (
                            <S.Section id={sectionDomId('settings')}>
                                <S.SectionHeaderRow>
                                    <S.SectionTitle>Settings</S.SectionTitle>
                                    <SectionStatusPill sectionId="settings" claimEditorStore={claimEditorStore} />
                                </S.SectionHeaderRow>
                                <SettingsForm claimEditorStore={claimEditorStore} />
                            </S.Section>
                        )}

                        {customSections}
                    </S.Content>
                </S.MainColumn>
            </S.Layout>

            <ClaimEditorStickyFooter
                claimEditorStore={claimEditorStore}
                title={pageTitle}
                submitLabel={submitLabel}
                isSubmitting={isSubmitting || claimEditorStore?.isLoading}
                onSubmit={handleSubmit}
                onCancel={onCancel}
                headerActions={headerActions}
            />

            {errorMessage && (
                <BaseDialog
                    open={true}
                    disableBackdropClick={true}
                    disableEscapeKeyDown={true}
                    renderTitle="Cannot submit claim"
                    renderActions={renderActions()}>
                    <ErrorStyles.DialogBody>
                        <ErrorStyles.Col>
                            {errorMessage}
                            <ErrorStyles.Textarea
                                ref={textRef}
                                rows={11}
                                value={errorMessage}
                                onClick={selectAll}
                                readOnly={true}
                            />
                        </ErrorStyles.Col>
                    </ErrorStyles.DialogBody>
                </BaseDialog>
            )}
        </S.Container>
    );
});

export const ClaimEditor: React.FunctionComponent<IClaimEditorProps> = props => {
    return (
        <Latin1ValidationProvider>
            <ClaimEditorContent {...props} />
        </Latin1ValidationProvider>
    );
};
